'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { formatPrice, isValidPhoneNumber } from '@/utils/util'

export type PaymentMethod = 'qpay' | 'transfer' | 'storepay' | 'pocket' | 'lend' | 'omniway' | 'onDelivery'

type ValidationResult = { ok: true } | { ok: false; error: string }

export function usePayment({ vendor, products, isFreeOrder, hasDeposit }: { vendor: any; products: any[]; isFreeOrder?: boolean; hasDeposit?: boolean }) {
  const [selectedPayment, setSelectedPayment] = useState<PaymentMethod | null>(null)
  const [storepayPhone, setStorepayPhone] = useState('')
  const [lendPhone, setLendPhone] = useState('')

  useEffect(() => {
    if (isFreeOrder) setSelectedPayment('transfer')
  }, [isFreeOrder])

  // Digital files are released on payment, so there's no handover to collect at.
  // The backend rejects 'onDelivery' for these carts too.
  const hasDigitalProduct = useMemo(() => products.some((p: any) => p?.isDigital), [products])

  const canPayOnDelivery = vendor?.canOnDelivery === true && !hasDigitalProduct

  /*
    Split payment: the BNPL rails cannot take a deposit.

    They underwrite a purchase and disburse the whole loan to the merchant; financing a
    fraction and then owing the rest in cash ten days later is two overlapping credit
    arrangements on one order. Their 100,000₮ floor would reject almost every deposit
    anyway. The backend excludes them from DEPOSIT_CAPABLE_METHODS for the same reasons.

    Enforced here rather than in the templates on purpose: all three already call this
    per row AND the auto-select precedence below routes through it, so the exclusion
    propagates with no template edits and no risk of pre-selecting a hidden row.
  */
  const DEPOSIT_BLOCKED_METHODS: PaymentMethod[] = ['storepay', 'pocket', 'lend', 'omniway']

  const isMethodAllowedForCart = useCallback(
    (key: PaymentMethod) => {
      if (key === 'onDelivery' && hasDigitalProduct) return false
      if (hasDeposit && DEPOSIT_BLOCKED_METHODS.includes(key)) return false
      return products.every((p: any) => p.paymentOptions?.[key] !== false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products, hasDigitalProduct, hasDeposit]
  )

  /*
    Auto-select precedence:

      onDelivery → qpay → storepay → pocket → lend → omniway → transfer

    Deliberately NOT the same as the render order in cart/Checkout.tsx, which lists
    transfer second. What gets pre-selected is a question about which method converts
    best, not about which row sits highest — transfer is the fallback for a store with
    no integrations, so it goes last. Aligning the two once looked like a tidy-up and
    silently changed the default payment method for every store with StorePay, Pocket
    or LendMN configured.

    Pay-after-delivery is the one method that outranks qpay: a vendor only turns it on
    because it is how their customers prefer to buy.
  */
  useEffect(() => {
    if (!selectedPayment) {
      if (canPayOnDelivery && isMethodAllowedForCart('onDelivery')) {
        setSelectedPayment('onDelivery')
      } else if (vendor?.qpayMerchant && vendor?.canQpay !== false && isMethodAllowedForCart('qpay')) {
        setSelectedPayment('qpay')
      } else if (vendor?.storepayCredentials && vendor?.canStorepay !== false && isMethodAllowedForCart('storepay')) {
        setSelectedPayment('storepay')
      } else if (vendor?.pocketCredentials && vendor?.canPocket !== false && isMethodAllowedForCart('pocket')) {
        setSelectedPayment('pocket')
      } else if (vendor?.lendCredentials && vendor?.canLend !== false && isMethodAllowedForCart('lend')) {
        setSelectedPayment('lend')
      } else if (vendor?.omniwayCredentials && vendor?.canOmniway !== false && isMethodAllowedForCart('omniway')) {
        setSelectedPayment('omniway')
      } else if (vendor?.canTransfer && vendor?.canSimple !== false && isMethodAllowedForCart('transfer')) {
        setSelectedPayment('transfer')
      }
    } else if (!isMethodAllowedForCart(selectedPayment)) {
      setSelectedPayment(null)
    }
  }, [vendor, selectedPayment, isMethodAllowedForCart, canPayOnDelivery])

  const selectPayment = (method: PaymentMethod) => {
    setSelectedPayment(method)
    if (method !== 'storepay') setStorepayPhone('')
    if (method !== 'lend') setLendPhone('')
  }

  /*
    payNowAmount is what this checkout actually charges — the deposit on a split payment,
    the full total otherwise. The BNPL thresholds have to measure against that, not
    against an order value the customer is not paying today.
  */
  const validatePayment = ({ totalPrice, isFreeOrder, payNowAmount }: { totalPrice: number; isFreeOrder: boolean; payNowAmount?: number }): ValidationResult => {
    const chargedNow = payNowAmount ?? totalPrice
    if (hasDeposit && selectedPayment && DEPOSIT_BLOCKED_METHODS.includes(selectedPayment)) {
      return { ok: false, error: 'Урьдчилгаатай захиалгад энэ төлбөрийн аргыг ашиглах боломжгүй' }
    }
    if (!isFreeOrder) {
      if (!selectedPayment) return { ok: false, error: 'Төлбөр төлөх аргыг сонгоно уу' }
      if (selectedPayment === 'transfer') {
        if (!vendor?.bank || !vendor.bank.accountName || !vendor.bank.accountNumber || !vendor.bank.bankName) {
          return { ok: false, error: 'Дэлгүүрт дансны мэдээлэл бүртгэгдээгүй байна. Өөр төлбөрийн аргыг сонгоно уу' }
        }
      }
    }
    if (selectedPayment === 'storepay') {
      if (!storepayPhone) return { ok: false, error: 'StorePay утасны дугаараа оруулна уу' }
      if (!isValidPhoneNumber(storepayPhone)) return { ok: false, error: 'StorePay утасны дугаар буруу байна' }
      if (chargedNow < 100000) return { ok: false, error: `StorePay ашиглахын тулд захиалгын дүн ${formatPrice(100000)}-с дээш байх шаардлагатай` }
    }
    if (selectedPayment === 'pocket') {
      if (chargedNow < 100000) return { ok: false, error: `Pocket ашиглахын тулд захиалгын дүн ${formatPrice(100000)}-с дээш байх шаардлагатай` }
    }
    if (selectedPayment === 'lend') {
      if (!lendPhone) return { ok: false, error: 'LendDy утасны дугаараа оруулна уу' }
      if (!isValidPhoneNumber(lendPhone)) return { ok: false, error: 'LendDy утасны дугаар буруу байна' }
    }
    if (selectedPayment === 'omniway') {
      if (chargedNow < 100000) return { ok: false, error: `OmniWay ашиглахын тулд захиалгын дүн ${formatPrice(100000)}-с дээш байх шаардлагатай` }
    }
    return { ok: true }
  }

  return useMemo(() => ({
    selectedPayment,
    storepayPhone, setStorepayPhone,
    lendPhone, setLendPhone,
    isMethodAllowedForCart,
    canPayOnDelivery,
    selectPayment,
    validatePayment,
  }), [selectedPayment, storepayPhone, lendPhone, isMethodAllowedForCart, canPayOnDelivery])
}
